import map from 'lodash/map'
import orderBy from 'lodash/orderBy'
import React from 'react'
import ErrorLabel from 'ui/atoms/ErrorLabel'
import Icons from 'ui/Icons'
import TextField from 'ui/molecules/TextField'
import PrimaryButton from 'ui/PrimaryButton'
import { HelperText, QuestionTitle } from 'ui/Text'
import { hasText } from 'utils/helpers'

import { TriggerUserActions } from '@/components/TriggerUserActions'
import { useFormAppearance } from '@/hooks/useFormAppearance'
import cn from '@/utils/tailwind/cn'

const HighlightHeaderClasses =
  'py-3 text-left text-sm leading-4 font-medium text-txt-emphasis tracking-wider'

const HighlightHeaderClassesMobile =
  'px-3 py-3 border-b border-gray-200 bg-gray-50 text-left text-sm leading-4 font-medium text-txt-emphasis tracking-wider'

export function TableOpenEndedRender({
  questionId,
  errorMessage,
  getCellValue = () => '',
  handleCellChange = () => {},
  onRemoveTableRow = () => {},
  onAddTableRow = () => {},
  hasError,
  help_text,
  isDirty,
  isOfficeUse,
  isRequired,
  isValid,
  label,
  question_columns,
  question_rows,
  readonly,
  willShowNotAllowedCursor,
  addRowToTableInBuilder,
  builderMode,
  response,
  sessions,
}) {
  const { hasQuestionColor } = useFormAppearance()
  function isRowError(rowId) {
    return question_columns.every((c) => !getCellValue(rowId, c.id))
  }

  function addTableRow() {
    if (builderMode) {
      return addRowToTableInBuilder()
    }
    return onAddTableRow()
  }

  return (
    <div>
      {label && (
        <QuestionTitle isOfficeUse={isOfficeUse} isRequired={isRequired}>
          {label}
        </QuestionTitle>
      )}
      {hasText(help_text) && (
        <HelperText className="mb-2" marginTop={0}>
          {help_text}
        </HelperText>
      )}

      {/* small devices */}
      <div className="divide-y divide-gray-200 overflow-hidden rounded border border-gray-200 md:hidden">
        {map(question_rows, (r, i) => (
          <div className="mb-3" key={r.id}>
            <div
              className={cn(
                hasQuestionColor && 'question-text',
                'border-b border-gray-200 bg-gray-50',
                HighlightHeaderClassesMobile,
                {
                  'text-red-500':
                    isDirty && isRequired && !isValid && isRowError(r.id),
                  'text-txt':
                    !isDirty || !isRequired || isValid || !isRowError(r.id),
                }
              )}
            >
              <span className="font-medium">{i + 1}</span>
            </div>

            <div className="mx-2 mt-2 space-y-1">
              {orderBy(question_columns, ['order'], ['asc']).map((c, i) => (
                <div className="px-3 py-1" key={c.id}>
                  <TriggerUserActions>
                    <TextField
                      data-form-id={response?.form?.id}
                      data-packet-id={
                        sessions?.fillResponseCustomer?.packetResponse?.packet
                          ?.id
                      }
                      data-response-id={response?.id}
                      disabled={readonly}
                      id={`q-${questionId}`}
                      label={c.header}
                      onChange={(v) => handleCellChange(r, c, v, r.local)}
                      value={getCellValue(r.id, c.id)}
                      willShowNotAllowedCursor={willShowNotAllowedCursor}
                    />
                  </TriggerUserActions>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="flex bg-white">
          <PrimaryButton className="mb-3 ml-4 mt-3" onClick={addTableRow}>
            <Icons.PlusCircle className="mr-2" color="text-white" />
            <span>Add Item</span>
          </PrimaryButton>
        </div>
      </div>

      {/* medium and above */}
      <div className="hidden overflow-hidden rounded border border-gray-200 md:block">
        {/* HEADER */}
        <div className="flex space-x-4 border-b border-gray-200 bg-gray-50 pr-3">
          <div className={`pl-3 ${HighlightHeaderClasses}`}>
            {!readonly && <div className="w-5" />}
          </div>
          {/* NUMBERING */}
          <div
            className={cn(
              hasQuestionColor && 'question-text',
              'w-5',
              HighlightHeaderClasses
            )}
          />
          {orderBy(question_columns, ['order'], ['asc']).map((c) => (
            <div
              className={cn(
                hasQuestionColor && 'question-text',
                'flex-1',
                HighlightHeaderClasses,
                'flex flex-col justify-center text-center'
              )}
              key={c.id}
            >
              <span>{c.header}</span>
              {hasText(c.help_text) && (
                <span className="mt-1 text-xs font-normal text-txt">
                  {c.help_text}
                </span>
              )}
            </div>
          ))}
        </div>
        {/* BODY */}
        <div className="divide-y divide-gray-200 bg-white">
          {map(question_rows, (r, i) => (
            <div
              className={`flex space-x-4 pr-3 ${
                i % 2 === 0 ? 'bg-white' : 'bg-gray-50'
              }`}
              key={r.id}
            >
              {r.local ? (
                <div
                  className={cn(
                    hasQuestionColor && 'question-text',
                    'flex cursor-pointer items-center pl-3',
                    HighlightHeaderClasses
                  )}
                  onClick={() =>
                    !readonly && onRemoveTableRow(r.id, r.local ?? false)
                  }
                >
                  {!readonly && (
                    <Icons.CloseCircleFilled color="hover:text-gray-700 text-gray-500" />
                  )}
                </div>
              ) : (
                <div className={`pl-3 ${HighlightHeaderClasses}`}>
                  {!readonly && <div className="w-5" />}
                </div>
              )}
              {/* NUMBERING */}
              <div
                className={cn(
                  hasQuestionColor && 'question-text',
                  'flex w-5 items-center justify-center text-sm text-txt'
                )}
              >
                {i + 1}
              </div>
              {orderBy(question_columns, ['order'], ['asc']).map((c) => (
                <div
                  className={cn(
                    hasQuestionColor && 'question-text',
                    'flex-1',
                    HighlightHeaderClasses,
                    {
                      'bg-white': i % 2 === 0,
                      'bg-gray-50': i % 2 !== 0,
                    }
                  )}
                  key={c.id}
                >
                  <TriggerUserActions>
                    <TextField
                      data-form-id={response?.form?.id}
                      data-packet-id={
                        sessions?.fillResponseCustomer?.packetResponse?.packet
                          ?.id
                      }
                      data-response-id={response?.id}
                      disabled={readonly}
                      inputId={questionId ? `q-${questionId}-c-${c.id}` : null}
                      onChange={(v) => handleCellChange(r, c, v, r.local)}
                      value={getCellValue(r.id, c.id)}
                      willShowNotAllowedCursor={willShowNotAllowedCursor}
                    />
                  </TriggerUserActions>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="flex space-x-4 bg-white">
          <div
            className={cn(
              hasQuestionColor && 'question-text',
              'pl-3',
              HighlightHeaderClasses
            )}
          >
            <div className="w-5" />
          </div>
          {/* NUMBERING */}
          <div
            className={cn(
              hasQuestionColor && 'question-text',
              'w-5',
              HighlightHeaderClasses
            )}
          />
          <PrimaryButton
            className="mb-3 ml-3 mt-3"
            disabled={readonly}
            onClick={addTableRow}
          >
            <Icons.PlusCircle className="mr-2" color="text-white" />
            <span>Add Item</span>
          </PrimaryButton>
        </div>
      </div>
      {hasError && (
        <div>
          <ErrorLabel>{errorMessage}</ErrorLabel>
        </div>
      )}
    </div>
  )
}

export default TableOpenEndedRender
